/**
 * PREAnalytics.jsx - Result Confidence Dashboard
 * 
 * Revamped confidence view that clearly communicates:
 * 1. Overall confidence level with visual gauge
 * 2. Three key pillars: Agreement, Stability, Variability
 * 3. Plain-English interpretation
 * 4. Collapsible advanced diagnostics
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Loader2,
  Shield,
  BarChart3,
  Target,
  Activity,
  Users,
  TrendingUp,
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';
import AdvancedDiagnostics from './reporting/AdvancedDiagnostics';
import CompareAggregateMethods from './reporting/CompareAggregateMethods';
import QuestionFitCard from './reporting/QuestionFitCard';
import ReliabilitySummary from './reporting/ReliabilitySummary';
import { buildReliabilitySummary } from '../lib/reliabilityUtils';
import { METRIC_HELP } from '../lib/metricLabels';

// ============================================================================
// CONFIDENCE LEVEL CONFIGURATION
// ============================================================================
const CONFIDENCE_LEVELS = {
  high: {
    label: 'High',
    color: 'text-positive-soft-foreground',
    bg: 'bg-positive-soft',
    border: 'border-positive-border',
    ringColor: 'ring-positive',
    progressColor: 'bg-positive',
    icon: CheckCircle2,
    iconColor: 'text-positive',
    score: 85,
    explanation: 'Responses are consistent across the population, with no strong instability across segments. Results are suitable for decision-making.',
    pillars: {
      agreement: { label: 'Strong', sublabel: 'High consensus across respondents' },
      stability: { label: 'High', sublabel: 'Results hold across demographic segments' },
      variability: { label: 'Expected', sublabel: 'Variation is within normal range' },
    }
  },
  medium: {
    label: 'Moderate',
    color: 'text-warning-soft-foreground',
    bg: 'bg-warning-soft',
    border: 'border-warning-border',
    ringColor: 'ring-warning',
    progressColor: 'bg-warning',
    icon: AlertTriangle,
    iconColor: 'text-warning',
    score: 60,
    explanation: 'Responses show some variation across the population. Results are directionally reliable but consider segment differences when making decisions.',
    pillars: {
      agreement: { label: 'Moderate', sublabel: 'Mixed consensus across respondents' },
      stability: { label: 'Moderate', sublabel: 'Some segments diverge from the mean' },
      variability: { label: 'Some variation', sublabel: 'Higher than typical spread' },
    }
  },
  low: {
    label: 'Low',
    color: 'text-negative-soft-foreground',
    bg: 'bg-negative-soft',
    border: 'border-negative-border',
    ringColor: 'ring-negative',
    progressColor: 'bg-negative',
    icon: XCircle,
    iconColor: 'text-negative',
    score: 35,
    explanation: 'Responses vary significantly across the population. Use results directionally and consider testing additional scenarios.',
    pillars: {
      agreement: { label: 'Weak', sublabel: 'Little agreement among respondents' },
      stability: { label: 'Low', sublabel: 'Results shift across segments' },
      variability: { label: 'High variation', sublabel: 'Significant spread in responses' },
    }
  },
};

// Calculate confidence level from PRE data
function calculateConfidenceLevel(preData) {
  if (!preData) return 'medium';
  
  const avgPMax = preData.avg_p_max || 0;
  const fragility = preData.fragility_index?.score || 50;
  const maxDivergence = preData.max_divergence || 0;
  
  if (avgPMax >= 0.55 && fragility < 40 && maxDivergence < 15) return 'high';
  if (avgPMax < 0.35 || fragility > 70 || maxDivergence > 25) return 'low';
  return 'medium';
}

// Calculate a numeric confidence score (0-100) from PRE data
function calculateConfidenceScore(preData) {
  if (!preData) return 60;
  
  const avgPMax = preData.avg_p_max || 0;
  const fragility = preData.fragility_index?.score || 50;
  const maxDivergence = preData.max_divergence || 0;
  
  // Weighted formula: agreement (40%), stability (35%), low variability (25%)
  const agreementScore = Math.min(100, avgPMax * 120); // 0-100, boosted
  const stabilityScore = Math.max(0, 100 - fragility);
  const variabilityScore = Math.max(0, 100 - maxDivergence * 3);
  
  return Math.round(agreementScore * 0.4 + stabilityScore * 0.35 + variabilityScore * 0.25);
}

// ============================================================================
// RESPONSE DISTRIBUTION UTILITIES
// ============================================================================
const DIST_COLORS = {
  strong_negative: '#dc2626',
  weak_negative: '#f87171',
  neutral: '#94a3b8',
  weak_positive: '#4ade80',
  strong_positive: '#16a34a',
};

function classifyOption(option, index, total) {
  const l = option.toLowerCase().trim();
  // Check for strong intensifiers: 'strongly', 'very', 'extremely'
  const hasStrongIntensifier = l.includes('strongly') || l.includes('very') || l.includes('extremely');
  
  if (hasStrongIntensifier && (l.includes('disagree') || l.includes('unfavorable') || l.includes('oppose'))) return 'strong_negative';
  if (l.includes('disagree') || l.includes('unfavorable') || l.includes('oppose') || l.includes('unlikely')) return 'weak_negative';
  if (l.includes('neutral') || l.includes('neither') || l.includes('undecided')) return 'neutral';
  if (hasStrongIntensifier && (l.includes('agree') || l.includes('favorable') || l.includes('support'))) return 'strong_positive';
  if (l.includes('agree') || l.includes('favorable') || l.includes('support') || l.includes('likely')) return 'weak_positive';
  // Fallback: use position in scale
  if (total >= 3) {
    const mid = Math.floor(total / 2);
    if (total % 2 === 1 && index === mid) return 'neutral';
    if (index < mid) return index === 0 && total >= 4 ? 'strong_negative' : 'weak_negative';
    return index === total - 1 && total >= 4 ? 'strong_positive' : 'weak_positive';
  }
  return 'neutral';
}

function computeDistributionMetrics(preData) {
  const options = preData.response_options || [];
  const dist = preData.expected_distribution || {};
  const n = options.length;

  const classified = options.map((opt, i) => ({
    label: opt,
    pct: dist[opt] || 0,
    sentiment: classifyOption(opt, i, n),
    index: i,
  }));

  const positivePct = classified.filter(c => c.sentiment.includes('positive')).reduce((s, c) => s + c.pct, 0);
  const negativePct = classified.filter(c => c.sentiment.includes('negative')).reduce((s, c) => s + c.pct, 0);
  const neutralPct = classified.filter(c => c.sentiment === 'neutral').reduce((s, c) => s + c.pct, 0);
  const netSupport = positivePct - negativePct;

  // Mean Score: weighted average using 1-based index
  let weightedSum = 0;
  let totalPct = 0;
  classified.forEach((c, i) => {
    weightedSum += (i + 1) * c.pct;
    totalPct += c.pct;
  });
  const meanScore = totalPct > 0 ? weightedSum / totalPct : 0;

  // Polarization: % extremes + std deviation composite
  const extremesPct = classified
    .filter(c => c.sentiment === 'strong_negative' || c.sentiment === 'strong_positive')
    .reduce((s, c) => s + c.pct, 0);
  let variance = 0;
  classified.forEach((c, i) => {
    variance += c.pct * Math.pow((i + 1) - meanScore, 2);
  });
  variance = totalPct > 0 ? variance / totalPct : 0;
  const stdDev = Math.sqrt(variance);
  const maxStd = (n - 1) / 2;
  const normalizedStd = maxStd > 0 ? (stdDev / maxStd) * 100 : 0;
  const polarizationScore = extremesPct * 0.6 + normalizedStd * 0.4;
  let polarizationLabel = 'Low';
  if (polarizationScore > 60) polarizationLabel = 'High';
  else if (polarizationScore > 30) polarizationLabel = 'Moderate';

  // Shannon Entropy
  let entropy = 0;
  classified.forEach(c => {
    const p = c.pct / 100;
    if (p > 0) entropy -= p * Math.log2(p);
  });
  const maxEntropy = n > 1 ? Math.log2(n) : 1;
  const normalizedEntropy = maxEntropy > 0 ? (entropy / maxEntropy) * 100 : 0;
  let entropyLabel = 'Low diversity';
  if (normalizedEntropy > 60) entropyLabel = 'High diversity';
  else if (normalizedEntropy > 30) entropyLabel = 'Moderate';

  return {
    netSupport, meanScore, scaleMax: n,
    polarizationScore, polarizationLabel,
    entropy, normalizedEntropy, entropyLabel,
    positivePct, negativePct, neutralPct,
    classified, extremesPct, stdDev,
  };
}

function getStructuralInterpretation(metrics) {
  const lines = [];
  if (metrics.normalizedEntropy > 60 && !metrics.classified.some(c => c.pct > 40)) {
    lines.push('Opinions are fragmented with no clear majority.');
  }
  if (metrics.negativePct - metrics.positivePct > 10) {
    lines.push('Resistance outweighs support.');
  } else if (metrics.positivePct - metrics.negativePct > 10) {
    lines.push('Support outweighs resistance.');
  }
  if (metrics.extremesPct > 50) {
    lines.push('High polarization detected.');
  }
  if (metrics.netSupport > 20) {
    lines.push('Strong positive consensus.');
  } else if (metrics.netSupport < -20) {
    lines.push('Strong negative consensus.');
  }
  if (metrics.polarizationLabel === 'High' && metrics.entropyLabel === 'High diversity') {
    lines.push('Population is deeply divided.');
  }
  if (lines.length === 0) {
    lines.push('Distribution shows moderate spread across response categories.');
  }
  return lines;
}

// ============================================================================
// CONFIDENCE BADGE - Reusable across the app
// ============================================================================
export function ConfidenceBadge({ level = 'medium', size = 'sm', showLabel = true }) {
  const config = CONFIDENCE_LEVELS[level] || CONFIDENCE_LEVELS.medium;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={cn(
        "gap-1 font-medium",
        config.bg, config.border, config.color,
        size === 'sm' && "text-xs px-2 py-0.5",
        size === 'md' && "text-sm px-2.5 py-1"
      )}
    >
      <Icon className={cn("w-3 h-3", config.iconColor)} />
      {showLabel && `${config.label} confidence`}
    </Badge>
  );
}

// ============================================================================
// MAIN PRE ANALYTICS COMPONENT
// ============================================================================
export default function PREAnalytics({ simulationId, simulation, aggregation = null, orderedResponseScale = [] }) {
  const [preData, setPreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!simulationId) return;

    const fetchPREData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/simulations/${simulationId}/pre-analytics`, { _silent: true });
        setPreData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPREData();
  }, [simulationId]);

  if (loading) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 mt-3">Loading confidence analysis...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="default" className="border-slate-200">
        <Info className="h-4 w-4" />
        <AlertDescription>
          {error.includes('PRE mode') 
            ? 'Confidence analysis is only available for simulations run in PRE mode.'
            : 'Unable to load confidence analysis.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!preData?.has_pre_data) {
    return (
      <Alert variant="default" className="border-slate-200">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Confidence analysis not available for this simulation. Run a simulation with PRE mode enabled.
        </AlertDescription>
      </Alert>
    );
  }

  const confidenceLevel = calculateConfidenceLevel(preData);
  const confidenceScore = calculateConfidenceScore(preData);
  const config = CONFIDENCE_LEVELS[confidenceLevel];
  const Icon = config.icon;

  // Raw metrics for diagnostics
  const agreementPct = ((preData.avg_p_max || 0) * 100).toFixed(1);
  const fragility = preData.fragility_index?.score || 0;
  const maxDivergence = preData.max_divergence || 0;

  // Build the canonical reliability summary — same shape used by PeopleLab.
  // Ensures BOTH surfaces show the same score / level / pillars for a given simulation.
  const sharedSummary = buildReliabilitySummary({ simulation, preData, peopleLabStability: null, diagnostics: null });

  return (
    <div className="space-y-5" data-testid="pre-analytics">
      {/* CANONICAL RELIABILITY SUMMARY — coherent with PeopleLab's "Reliability & Assumptions" */}
      <ReliabilitySummary
        mode="full"
        simulation={simulation}
        preData={preData}
        summary={sharedSummary}
      />

      {/* PRE-SPECIFIC RAW METRICS — supplementary detail unique to PRE-mode runs */}
      <Card className="border border-slate-200 dark:border-slate-700" data-testid="pre-raw-metrics-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-400" />
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              PRE Raw Metrics
            </CardTitle>
            <Badge variant="outline" className="text-[10px] font-medium ml-1">{preData.total_responses?.toLocaleString()} responses</Badge>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Per-respondent probability metrics (only available for PRE-mode runs).
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <PillarCard
              icon={Target}
              title="Agreement (P-Max)"
              value={config.pillars.agreement.label}
              sublabel={config.pillars.agreement.sublabel}
              metric={`${agreementPct}%`}
              metricLabel="avg confidence"
              level={confidenceLevel}
            />
            <PillarCard
              icon={Activity}
              title="Stability"
              value={config.pillars.stability.label}
              sublabel={config.pillars.stability.sublabel}
              metric={`${(100 - fragility).toFixed(0)}%`}
              metricLabel="100 − fragility"
              level={confidenceLevel}
            />
            <PillarCard
              icon={TrendingUp}
              title="Variability"
              value={config.pillars.variability.label}
              sublabel={config.pillars.variability.sublabel}
              metric={`${maxDivergence.toFixed(1)}pp`}
              metricLabel="max divergence"
              level={confidenceLevel}
            />
          </div>
        </CardContent>
      </Card>

      {/* ─── Decision Guidance ─────────────────────────── */}
      {(() => {
        let rec, recIcon, recColor, recBg, recBorder, recDetail;
        if (confidenceLevel === 'high' && fragility < 35) {
          rec = 'Stable for broad interpretation';
          recIcon = CheckCircle2;
          recColor = 'text-positive-soft-foreground';
          recBg = 'bg-positive-soft';
          recBorder = 'border-positive-border';
          recDetail = 'Results are stable enough for broad interpretation. Method, response certainty, and segment stability all hold up.';
        } else if (confidenceLevel === 'low' || fragility > 65 || maxDivergence > 22) {
          rec = 'Validate before acting';
          recIcon = XCircle;
          recColor = 'text-negative-soft-foreground';
          recBg = 'bg-negative-soft';
          recBorder = 'border-negative-border';
          recDetail = 'Results are sensitive. Validate with additional scenarios or real-world data before making decisions.';
        } else {
          rec = 'Directionally useful — mind the segments';
          recIcon = AlertTriangle;
          recColor = 'text-warning-soft-foreground';
          recBg = 'bg-warning-soft';
          recBorder = 'border-warning-border';
          recDetail = 'Results are directionally useful, but segment differences matter. Pay attention to demographic breakdowns before acting.';
        }
        const RecIcon = recIcon;
        return (
          <div className={cn("flex items-start gap-3 p-4 rounded-lg border-2", recBg, recBorder)} data-testid="decision-guidance">
            <RecIcon className={cn("w-5 h-5 mt-0.5 flex-shrink-0", recColor)} />
            <div>
              <p className={cn("text-sm font-bold", recColor)}>{rec}</p>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{recDetail}</p>
            </div>
          </div>
        );
      })()}
      {preData.expected_distribution && preData.response_options?.length > 0 && (
        <ResponseDistributionStructure
          preData={preData}
          confidenceLevel={confidenceLevel}
          fragility={fragility}
        />
      )}

      {/* Distribution Diversity — always-visible simulation-level metrics */}
      {(simulation?.normalized_entropy != null || simulation?.mean_probability_entropy != null) && (
        <Card className="border border-slate-200 dark:border-border" data-testid="distribution-diversity-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-400" />
              <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Distribution Diversity
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="distribution-diversity-metrics">
              {simulation?.normalized_entropy != null && (() => {
                const v = simulation.normalized_entropy;
                const pct = (v * 100).toFixed(0);
                const label = v >= 0.7 ? 'Broad spread' : v >= 0.35 ? 'Moderate spread' : 'Narrow spread';
                return (
                  <MetricCell
                    label="Response spread (aggregate)"
                    value={`${pct}%`}
                    sublabel={label}
                    data-testid="metric-normalized-entropy"
                  />
                );
              })()}
              {simulation?.mean_probability_entropy != null && (() => {
                const v = simulation.mean_probability_entropy;
                const pct = (v * 100).toFixed(0);
                const label = v >= 0.7 ? 'High per-agent uncertainty' : v >= 0.35 ? 'Moderate per-agent uncertainty' : 'Low per-agent uncertainty';
                return (
                  <MetricCell
                    label="Per-agent uncertainty"
                    value={`${pct}%`}
                    sublabel={label}
                    data-testid="metric-mean-probability-entropy"
                  />
                );
              })()}
            </div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
              <strong>Response spread:</strong> how evenly the population's responses split across options (0% = everyone picks the same, 100% = perfectly even).{' '}
              <strong>Per-agent uncertainty:</strong> on average, how undecided each individual was between the options.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Question Fit Analysis (new) */}
      <QuestionFitCard simulationId={simulationId} />

      {/* Compare Aggregate Methods (relocated from Overview) */}
      {aggregation && (
        <CompareAggregateMethods
          aggregation={aggregation}
          orderedResponseScale={orderedResponseScale}
        />
      )}

      {/* Advanced Diagnostics — shared collapsible wrapper */}
      <AdvancedDiagnostics
        title="Advanced Diagnostics"
        testId="pre-advanced-diagnostics"
      >
        {/* Raw Metrics Table — friendly labels with technical name in parens */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <MetricCell label="Response Certainty (P-Max)" value={`${agreementPct}%`} />
          <MetricCell label="Response Diversity" value={`${(100 - parseFloat(agreementPct)).toFixed(1)}%`} />
          <MetricCell label="Sensitivity (Fragility)" value={fragility.toFixed(0)} />
          <MetricCell label="Max Segment Difference" value={`${maxDivergence.toFixed(1)}pp`} />
          <MetricCell label="Individual Uncertainty (avg entropy)" value={preData.avg_individual_entropy?.toFixed(3) || '-'} />
          <MetricCell label="Total Responses" value={preData.total_responses || 0} />
        </div>

        {/* Fragility Components */}
        {preData.fragility_index?.components && (
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">Sensitivity Components</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(preData.fragility_index.components).map(([key, value]) => (
                <MetricCell key={key} label={key} value={`${value}%`} />
              ))}
            </div>
          </div>
        )}

        {/* Response Scale */}
        {preData.response_options && (
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">Response Scale</p>
            <div className="flex flex-wrap gap-1.5">
              {preData.response_options.map((opt, i) => (
                <Badge key={i} variant="outline" className="text-xs">{opt}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Interpretation Guide */}
        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Interpretation Guide</p>
          <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
            <p><strong>Response Certainty (P-Max):</strong> {METRIC_HELP.responseCertainty}</p>
            <p><strong>Sensitivity (Fragility):</strong> {METRIC_HELP.sensitivity}</p>
            <p><strong>Max Segment Difference:</strong> {METRIC_HELP.maxSegmentDifference}</p>
            <p><strong>Individual Uncertainty:</strong> {METRIC_HELP.individualUncertainty}</p>
          </div>
        </div>
      </AdvancedDiagnostics>
    </div>
  );
}

// ============================================================================
// RESPONSE DISTRIBUTION & STRUCTURE
// ============================================================================

function ResponseDistributionStructure({ preData, confidenceLevel, fragility }) {
  const [showVarianceBands, setShowVarianceBands] = useState(false);

  const metrics = useMemo(() => computeDistributionMetrics(preData), [preData]);
  const interpretations = useMemo(() => getStructuralInterpretation(metrics), [metrics]);

  const options = preData.response_options || [];
  const n = options.length;
  const midpoint = Math.floor(n / 2);
  const hasOddScale = n % 2 === 1;
  const { classified } = metrics;

  // Diverging chart segments
  const leftItems = classified.filter((_, i) => i < midpoint);
  const neutralItem = hasOddScale ? classified[midpoint] : null;
  const rightItems = classified.filter((_, i) => hasOddScale ? i > midpoint : i >= midpoint);
  const leftTotal = leftItems.reduce((s, c) => s + c.pct, 0);
  const rightTotal = rightItems.reduce((s, c) => s + c.pct, 0);
  const neutralPct = neutralItem?.pct || 0;
  const totalPct = leftTotal + neutralPct + rightTotal || 1;

  // Confidence integration
  const highVariability = fragility > 50 || metrics.normalizedEntropy > 60;
  const isPREMode = preData.has_pre_data;

  return (
    <Card className="border border-slate-200 dark:border-border" data-testid="response-distribution">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            Response Distribution & Structure
          </CardTitle>
          {isPREMode && (
            <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-200 text-blue-700">
              Probability-Weighted Distribution
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 1. Summary Metrics Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="distribution-metrics">
          <MetricChip
            label="Net Support"
            value={`${metrics.netSupport > 0 ? '+' : ''}${metrics.netSupport.toFixed(1)}`}
            color={metrics.netSupport > 0 ? 'text-positive-soft-foreground' : metrics.netSupport < 0 ? 'text-negative-soft-foreground' : 'text-slate-700'}
          />
          <MetricChip
            label="Mean Score"
            value={`${metrics.meanScore.toFixed(2)} / ${metrics.scaleMax}`}
          />
          <MetricChip
            label="Polarization"
            value={metrics.polarizationLabel}
            sublabel={`${metrics.polarizationScore.toFixed(0)}%`}
            color={metrics.polarizationLabel === 'High' ? 'text-negative-soft-foreground' : metrics.polarizationLabel === 'Moderate' ? 'text-warning-soft-foreground' : 'text-slate-700'}
          />
          <MetricChip
            label="Entropy"
            value={metrics.entropy.toFixed(2)}
            sublabel={metrics.entropyLabel}
          />
        </div>

        {/* 2. Diverging Likert Chart */}
        <div data-testid="diverging-likert-chart">
          <div className="flex justify-between text-[10px] text-slate-400 mb-1 px-1">
            <span>Negative</span>
            <span>Positive</span>
          </div>

          <div className="relative">
            <div className="flex items-center h-12 rounded overflow-hidden">
              {/* Left (negative) — most extreme outermost */}
              {leftItems.map((d) => {
                const width = (d.pct / totalPct) * 100;
                const color = DIST_COLORS[d.sentiment] || DIST_COLORS.neutral;
                const spread = preData.probability_spread?.[d.label];
                return (
                  <div
                    key={d.label}
                    className="h-full relative group cursor-default transition-all duration-300"
                    style={{ width: `${width}%`, backgroundColor: color }}
                    title={`${d.label}: ${d.pct.toFixed(1)}%`}
                  >
                    {d.pct >= 7 && (
                      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white drop-shadow-sm">
                        {d.pct.toFixed(0)}%
                      </span>
                    )}
                    {showVarianceBands && spread && (
                      <div
                        className="absolute top-0 bottom-0 border-l border-r border-white/60"
                        style={{
                          left: `${(spread.q25 / (spread.max || 1)) * 100}%`,
                          right: `${100 - (spread.q75 / (spread.max || 1)) * 100}%`,
                          backgroundColor: 'rgba(255,255,255,0.25)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
              {/* Neutral center */}
              {neutralItem && (
                <div
                  className="h-full relative"
                  style={{ width: `${(neutralPct / totalPct) * 100}%`, backgroundColor: DIST_COLORS.neutral }}
                  title={`${neutralItem.label}: ${neutralPct.toFixed(1)}%`}
                >
                  {neutralPct >= 6 && (
                    <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white drop-shadow-sm">
                      {neutralPct.toFixed(0)}%
                    </span>
                  )}
                </div>
              )}
              {/* Right (positive) */}
              {rightItems.map((d) => {
                const width = (d.pct / totalPct) * 100;
                const color = DIST_COLORS[d.sentiment] || DIST_COLORS.neutral;
                const spread = preData.probability_spread?.[d.label];
                return (
                  <div
                    key={d.label}
                    className="h-full relative group cursor-default transition-all duration-300"
                    style={{ width: `${width}%`, backgroundColor: color }}
                    title={`${d.label}: ${d.pct.toFixed(1)}%`}
                  >
                    {d.pct >= 7 && (
                      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white drop-shadow-sm">
                        {d.pct.toFixed(0)}%
                      </span>
                    )}
                    {showVarianceBands && spread && (
                      <div
                        className="absolute top-0 bottom-0 border-l border-r border-white/60"
                        style={{
                          left: `${(spread.q25 / (spread.max || 1)) * 100}%`,
                          right: `${100 - (spread.q75 / (spread.max || 1)) * 100}%`,
                          backgroundColor: 'rgba(255,255,255,0.25)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {/* Center line */}
            <div
              className="absolute top-0 bottom-0 w-px bg-slate-800/40"
              style={{ left: `${((leftTotal + neutralPct / 2) / totalPct) * 100}%` }}
            />
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-3 mt-2.5 flex-wrap">
            {classified.map((d) => (
              <div key={d.label} className="flex items-center gap-1.5 text-xs text-slate-600">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: DIST_COLORS[d.sentiment] }} />
                <span>{d.label}</span>
                <span className="text-slate-400 font-mono">{d.pct.toFixed(0)}%</span>
              </div>
            ))}
          </div>

          {/* Variance toggle */}
          <div className="flex justify-end mt-1">
            <button
              onClick={() => setShowVarianceBands(!showVarianceBands)}
              className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="toggle-variance-bands"
            >
              {showVarianceBands ? 'Hide' : 'Show'} Variance Bands
            </button>
          </div>
        </div>

        {/* 3. Negative / Neutral / Positive Summary Row */}
        <div className="grid grid-cols-3 gap-2" data-testid="sentiment-summary">
          <div className="flex items-center gap-2 p-2 rounded bg-negative-soft border border-negative-border">
            <div className="w-2 h-8 rounded-full bg-negative" />
            <div>
              <p className="text-[10px] text-negative-soft-foreground font-medium">Negative</p>
              <p className="text-sm font-bold text-negative-soft-foreground">{metrics.negativePct.toFixed(1)}%</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-border">
            <div className="w-2 h-8 rounded-full bg-slate-400" />
            <div>
              <p className="text-[10px] text-slate-600 font-medium">Neutral</p>
              <p className="text-sm font-bold text-slate-800">{metrics.neutralPct.toFixed(1)}%</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-positive-soft border border-positive-border">
            <div className="w-2 h-8 rounded-full bg-positive" />
            <div>
              <p className="text-[10px] text-positive-soft-foreground font-medium">Positive</p>
              <p className="text-sm font-bold text-positive-soft-foreground">{metrics.positivePct.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        {/* 4. Structural Interpretation Line */}
        <div className="px-3 py-2 bg-slate-50 dark:bg-muted rounded border border-slate-100 dark:border-border" data-testid="structural-interpretation">
          {interpretations.map((line, i) => (
            <p key={i} className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
              <span className="text-slate-400 mr-1">&mdash;</span>
              {line}
            </p>
          ))}
        </div>

        {/* 5. Confidence Integration */}
        {highVariability && (
          <div className="flex items-start gap-2 px-3 py-2 rounded bg-warning-soft/50 border border-warning-border" data-testid="confidence-link">
            <AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5 flex-shrink-0" />
            <p className="text-xs text-warning-soft-foreground">
              High variability contributes to lower stability score.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricChip({ label, value, sublabel, color = 'text-slate-800 dark:text-slate-100' }) {
  return (
    <div className="p-2 rounded bg-slate-50 dark:bg-muted border border-slate-100 dark:border-border text-center">
      <p className="text-[10px] text-slate-500 font-medium">{label}</p>
      <p className={cn("text-sm font-bold", color)}>{value}</p>
      {sublabel && <p className="text-[10px] text-slate-400">{sublabel}</p>}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function PillarCard({ icon: PillarIcon, title, value, sublabel, metric, metricLabel, level }) {
  const config = CONFIDENCE_LEVELS[level] || CONFIDENCE_LEVELS.medium;
  
  return (
    <div className="p-3 rounded-lg bg-white border border-slate-100 hover:border-slate-200 transition-colors">
      <div className="flex items-center gap-1.5 mb-2">
        <PillarIcon className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs font-medium text-slate-500">{title}</span>
      </div>
      <p className={cn("font-bold text-lg", config.color)}>{value}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">{sublabel}</p>
      <div className="mt-2 pt-2 border-t border-slate-50">
        <p className="text-xs text-slate-500">
          <span className="font-medium">{metric}</span>
          <span className="text-slate-400 ml-1">{metricLabel}</span>
        </p>
      </div>
    </div>
  );
}

function MetricCell({ label, value, sublabel, 'data-testid': testId }) {
  return (
    <div className="p-2 rounded bg-slate-50 border border-slate-100" data-testid={testId}>
      <p className="text-[10px] text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
      {sublabel && <p className="text-[10px] text-slate-500 mt-0.5">{sublabel}</p>}
    </div>
  );
}

// ============================================================================
// RESULTS OVERVIEW COMPONENT - For Analysis page header
// ============================================================================
export function ResultsOverview({ preData, simulationStatus }) {
  if (!preData?.has_pre_data) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <CheckCircle2 className="w-4 h-4 text-positive" />
        <span>Simulation completed with {preData?.total_responses || 'N/A'} responses</span>
      </div>
    );
  }

  const confidenceLevel = calculateConfidenceLevel(preData);

  return (
    <div className="flex items-center gap-3" data-testid="results-overview">
      <ConfidenceBadge level={confidenceLevel} size="md" />
      <span className="text-sm text-slate-600">
        based on {preData.total_responses} simulated responses
      </span>
    </div>
  );
}
